import { PRIVILEGES } from '@/constants/privileges'
import { SITE_LOCATIONS } from '@/constants/siteLocations.constants'

/**
 * masters `/v1/configurations` decides which buttons exist from the v1 session role, so a user
 * whose v3 privileges grant `order.add` (e.g. a v1 specifier that CRM makes a distributor) never
 * gets "place order". With `getUserByCrmIdV3` ON the button follows the privilege instead: it is
 * inserted where masters places it for a distributor, and removed for anyone without it.
 */
export const PLACE_ORDER_KEY = 'button.project.placeOrder'

// USER_ROLES.sales_usa (ui.constants.js): role id 7, or role "sales" on the USA catalog.
const SALES_USA_ROLE_ID = 7
const USA_CATALOG_ID = 21

const isSalesUsa = (account) =>
	Number(account?.rolId) === SALES_USA_ROLE_ID ||
	(account?.roles?.includes('sales') && Number(account?.catalog) === USA_CATALOG_ID)

export const canPlaceOrder = (account) =>
	!!account?.privileges?.includes(PRIVILEGES.ORDER_ADD) && !isSalesUsa(account)

const PLACEMENTS = {
	[SITE_LOCATIONS.PROJECT_DETAILS]: {
		ORDINARY: { button: { key: PLACE_ORDER_KEY, importance: 0 }, before: 'button.project.add' },
		CONTEXTUAL: { button: { key: PLACE_ORDER_KEY, importance: 1, icon: 'assign' }, first: true }
	},
	[SITE_LOCATIONS.MY_PROJECTS]: {
		ORDINARY: { button: { key: PLACE_ORDER_KEY, importance: 0 }, after: 'button.project.view' },
		CONTEXTUAL: null
	}
}

const insertionIndex = (list, placement) => {
	if (placement.first) return 0
	const anchorKey = placement.before ?? placement.after
	const anchor = list.findIndex((button) => button.key === anchorKey)
	if (anchor === -1) return list.length
	return placement.before ? anchor : anchor + 1
}

const insertPlaceOrder = (list, placement) => {
	if (!placement || list.some((button) => button.key === PLACE_ORDER_KEY)) return list
	const index = insertionIndex(list, placement)
	return [...list.slice(0, index), { ...placement.button }, ...list.slice(index)]
}

const removePlaceOrder = (list) => list.filter((button) => button.key !== PLACE_ORDER_KEY)

export const applyPlaceOrderPrivilege = (buttons, siteLocation, account) => {
	const placements = PLACEMENTS[siteLocation]
	if (!placements || !buttons) return buttons

	const allowed = canPlaceOrder(account)
	return Object.fromEntries(
		Object.entries(buttons).map(([category, list]) => {
			if (!Array.isArray(list)) return [category, list]
			return [category, allowed ? insertPlaceOrder(list, placements[category]) : removePlaceOrder(list)]
		})
	)
}
